import { index, pgTable, text, timestamp, unique, uuid, integer, boolean, vector } from 'drizzle-orm/pg-core'
import { sql } from 'drizzle-orm'
import { tenants } from './tenants'
import { users } from './users'

export const KNOWLEDGE_ENTRY_KINDS = ['index', 'log', 'entity', 'concept', 'procedure'] as const
export type KnowledgeEntryKind = typeof KNOWLEDGE_ENTRY_KINDS[number]

export const KNOWLEDGE_ENTRY_SOURCES = ['seed', 'user', 'llm'] as const
export type KnowledgeEntrySource = typeof KNOWLEDGE_ENTRY_SOURCES[number]

export const KNOWLEDGE_EMBEDDING_DIMENSIONS = 64
export const KNOWLEDGE_EMBEDDING_MODEL = 'local-hash-v1'

export const knowledgeEntries = pgTable(
  'knowledge_entries',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    slug: text('slug').notNull(),
    title: text('title').notNull(),
    kind: text('kind', { enum: KNOWLEDGE_ENTRY_KINDS }).notNull().default('concept'),
    bodyMarkdown: text('body_markdown').notNull(),
    flatMarkdown: text('flat_markdown').notNull(),
    source: text('source', { enum: KNOWLEDGE_ENTRY_SOURCES }).notNull().default('user'),
    isVerified: boolean('is_verified').notNull().default(false),
    embedding: vector('embedding', { dimensions: KNOWLEDGE_EMBEDDING_DIMENSIONS }),
    embeddingModel: text('embedding_model'),
    embeddingUpdatedAt: timestamp('embedding_updated_at', { withTimezone: true }),
    isDeleted: boolean('is_deleted').notNull().default(false),
    createdById: uuid('created_by_id').references(() => users.id),
    updatedById: uuid('updated_by_id').references(() => users.id),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqTenantSlug: unique('knowledge_entries_tenant_slug_unique').on(t.tenantId, t.slug),
    tenantUpdatedIdx: index('knowledge_entries_tenant_updated_idx').on(t.tenantId, t.updatedAt),
    embeddingIdx: index('knowledge_entries_embedding_idx')
      .using('hnsw', t.embedding.op('vector_cosine_ops'))
      .where(sql`embedding IS NOT NULL`),
  }),
)

export const knowledgeEntryRevisions = pgTable(
  'knowledge_entry_revisions',
  {
    id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id').notNull().references(() => tenants.id),
    entryId: uuid('entry_id').notNull().references(() => knowledgeEntries.id),
    revisionNumber: integer('revision_number').notNull(),
    title: text('title').notNull(),
    bodyMarkdown: text('body_markdown').notNull(),
    flatMarkdown: text('flat_markdown').notNull(),
    editedById: uuid('edited_by_id').references(() => users.id),
    changeReason: text('change_reason'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    uniqEntryRevision: unique('knowledge_entry_revisions_entry_revision_unique').on(
      t.entryId,
      t.revisionNumber,
    ),
    entryCreatedIdx: index('knowledge_entry_revisions_entry_created_idx').on(t.entryId, t.createdAt),
  }),
)
